import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, CreditCard, Headset, PartyPopper, BadgeCheck, Music, Loader2 } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import MollieCardPayment from "@/components/wallet/MollieCardPayment";
import SubscriptionPlans from "@/components/subscription/SubscriptionPlans";
import PurchaseStepsReassurance from "@/components/wizard/PurchaseStepsReassurance";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useTokenPurchase } from "@/hooks/useTokenPurchase";
import { formatPrice, songPriceLabel } from "@/utils/price";
import { translateApiError } from "@/utils/apiErrors";
import { useIsAndroidApp } from "@/hooks/useLaunchContext";
import api from "@/lib/api";
import { CHECKOUT } from "@/constants/testIds";
import SEO from "@/components/seo/SEO";

const SONG_PRICE_EUR = 9.99;

export default function Checkout() {
  const { orderId } = useParams();
  const { t, language } = useI18n();
  const { user, refetch } = useAuth();
  const { toast } = useToast();
  const { buyPack: purchasePack } = useTokenPurchase();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [payingGoogle, setPayingGoogle] = useState(false);
  const [showCardFallback, setShowCardFallback] = useState(false);
  const [subCollapse, setSubCollapse] = useState(0);
  const [subStatus, setSubStatus] = useState(null);
  const isTwaApp = useIsAndroidApp();
  const autoFinalizedRef = useRef(false);

  const refreshSubscription = () =>
    api.get("/subscriptions/me").then(({ data }) => setSubStatus(data)).catch(() => {});

  useEffect(() => {
    refreshSubscription();
  }, []);

  useEffect(() => {
    api.get(`/orders/${orderId}`).then(({ data }) => {
      if (data.status !== "pending_payment") {
        navigate(`/orders/${orderId}`);
        return;
      }
      setOrder(data);
    });
  }, [orderId, navigate]);

  // Promo première commande (ex: SUMMER2026 -50%) : appliquée automatiquement sur le web
  // uniquement (dans l'app Android, Google Play gère les prix - jamais de promo côté app).
  // Le backend vérifie l'éligibilité (1re commande, 1 seule fois par client) : tout refus est
  // silencieux, la commande reste au plein tarif. IMPORTANT (bug 20/08/2026) : le bloc de
  // paiement n'est rendu QU'APRÈS résolution du promo (promoReady) - sinon le changement de
  // total remonte les 4 champs Mollie en plein vol et le script externe mollie.js lève
  // 4 "Script error." cross-origin (overlay rouge en dev).
  const [promoReady, setPromoReady] = useState(false);
  const promoTriedRef = useRef(false);
  useEffect(() => {
    if (!order || promoTriedRef.current) return;
    promoTriedRef.current = true;
    if (isTwaApp || order.pricing?.promo_code) {
      setPromoReady(true);
      return;
    }
    (async () => {
      try {
        const { data: promo } = await api.get("/promo/active");
        if (promo?.code) {
          await api.post(`/orders/${orderId}/apply-promo`, { code: promo.code });
          const { data } = await api.get(`/orders/${orderId}`);
          setOrder(data);
        }
      } catch {
        // inéligible / pas de promo : plein tarif, sans erreur bloquante
      }
      setPromoReady(true);
    })();
  }, [order, isTwaApp, orderId]);

  // Rattrapage automatique : si un achat "1 musique" a déjà été encaissé pour cette commande
  // (ex: achat Google Play capturé tardivement pendant que l'app était fermée), on finalise
  // la commande tout seul, sans re-demander de paiement.
  useEffect(() => {
    if (!order || autoFinalizedRef.current) return;
    (async () => {
      try {
        const { data: payments } = await api.get("/payments");
        const paidSong = (payments || []).find(
          (p) => p.purpose === "song_purchase" && p.resume_order_id === orderId && p.status === "paid"
        );
        if (paidSong && !autoFinalizedRef.current) {
          autoFinalizedRef.current = true;
          await api.post("/wallet/spend", { order_id: orderId });
          await refetch();
          toast({ title: t("checkout.success") });
          navigate(`/orders/${orderId}`);
        }
      } catch { /* noop */ }
    })();
  }, [order]);

  if (!order) return null;

  const total = order.pricing.total;
  const promoDiscount = order.pricing.promo_discount || 0;
  const promoApplied = order.pricing.promo_code;
  const originalTotal = total + promoDiscount;
  const unitsNeeded = order.form?.num_versions || 1;
  const coveredBySub = !!(subStatus?.active && subStatus.remaining >= unitsNeeded);

  const finalizeOrder = async () => {
    await refetch();
    await api.post("/wallet/spend", { order_id: orderId });
    await refetch();
    toast({ title: t("checkout.success") });
    navigate(`/orders/${orderId}`);
  };

  // Abonné avec quota : la musique est couverte par le forfait (inchangé).
  const handlePayWithSubscription = async () => {
    setProcessing(true);
    try {
      await api.post("/wallet/spend", { order_id: orderId });
      await refetch();
      toast({ title: t("checkout.success") });
      navigate(`/orders/${orderId}`);
    } catch (err) {
      toast({ variant: "destructive", title: t("common.error"), description: translateApiError(err?.response?.data?.detail, language) });
    } finally {
      setProcessing(false);
    }
  };

  // App Android : fenêtre Google Play native (prix localisé géré par Google, aucun prix
  // affiché côté app). Dès le 1er échec -> bascule web automatique (déjà gérée dans le hook).
  const handlePayGooglePlay = async () => {
    setPayingGoogle(true);
    try {
      const data = await purchasePack(SONG_PRICE_EUR, { resumeOrderId: orderId, resumeAction: "spend", songPurchase: true });
      if (data.status === "paid") await finalizeOrder();
      else if (data.checkout_url && !data.redirected) window.location.assign(data.checkout_url);
    } catch (err) {
      if (err?.playBillingFallback) {
        toast({ title: t("checkout.playBillingFallbackTitle"), description: t("checkout.playBillingFallbackDesc") });
        setSubCollapse((n) => n + 1);
        setShowCardFallback(true);
      } else if (err?.isPurchaseCancelled) {
        toast({ title: t("checkout.playCancelledTitle"), description: t("checkout.playCancelledDesc") });
      } else {
        toast({ variant: "destructive", title: t("common.error"), description: translateApiError(err?.response?.data?.detail, language) || err?.message });
      }
    } finally {
      setPayingGoogle(false);
    }
  };

  const handleCardPaymentSuccess = async () => {
    setShowCardFallback(false);
    await finalizeOrder();
  };

  // Un abonnement vient d'être activé depuis ce checkout : son quota couvre la commande.
  const handleSubscribed = async () => {
    setProcessing(true);
    try {
      await api.post("/wallet/spend", { order_id: orderId });
      await refetch();
      toast({ title: t("checkout.success") });
      navigate(`/orders/${orderId}`);
    } catch (err) {
      await refreshSubscription();
      toast({ variant: "destructive", title: t("common.error"), description: translateApiError(err?.response?.data?.detail, language) || err?.message });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-lg px-4 py-16 sm:px-6">
      <SEO title="Finaliser ma commande | Kalypso Music" description="Finalisez la commande de votre chanson personnalisée en toute sécurité." path="/checkout" noindex />
        <button onClick={() => navigate("/create")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" />
          {t("checkout.backToWizard")}
        </button>

        <h1 className="mt-6 text-center font-heading text-2xl font-medium">{t("checkout.title")}</h1>

        <div className="mt-8 rounded-2xl border border-border bg-card p-6" data-testid={CHECKOUT.summary}>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm">
              <Music className="h-4 w-4 text-primary" />
              <span>
                <span className="block font-medium">{t("checkout.flatTitle")}</span>
                <span className="block text-xs text-muted-foreground">{t("checkout.allIncluded")}</span>
              </span>
            </span>
            {!isTwaApp && (
              <span className="flex items-center gap-2">
                {promoApplied && (
                  <span className="text-base text-muted-foreground line-through">{formatPrice(originalTotal, language)}</span>
                )}
                <span className="font-heading text-3xl font-semibold text-primary" data-testid="checkout-song-price">
                  {promoApplied ? formatPrice(total, language) : songPriceLabel(language)}
                </span>
              </span>
            )}
          </div>
          {promoApplied && !isTwaApp && (
            <div className="mt-2 flex items-center justify-end gap-1.5 text-sm font-medium text-success" data-testid={CHECKOUT.promoApplied}>
              <PartyPopper className="h-3.5 w-3.5" />
              -{formatPrice(promoDiscount, language)} ({t("checkout.promoDiscount")} {promoApplied})
            </div>
          )}
        </div>


        {coveredBySub ? (
          <div className="mt-6 space-y-3">
            <div className="rounded-2xl border border-success/40 bg-success/10 p-4 text-center" data-testid="checkout-covered-by-subscription">
              <p className="flex items-center justify-center gap-1.5 text-sm font-semibold text-success">
                <BadgeCheck className="h-4 w-4" />
                {t("subscription.coveredBySub")} {subStatus.plan_name}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("subscription.willRemain")} {Math.max(0, subStatus.remaining - unitsNeeded)} {t("subscription.creationsLeft")}
              </p>
            </div>
            <Button className="w-full rounded-full" size="lg" data-testid={CHECKOUT.payBtn} onClick={handlePayWithSubscription} disabled={processing}>
              <BadgeCheck className="mr-2 h-4 w-4" />
              {processing ? t("checkout.processing") : t("subscription.payWithSubscription")}
            </Button>
          </div>
        ) : !promoReady ? (
          <div className="mt-6 flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground" data-testid="checkout-loading-payment">
            <Loader2 className="h-4 w-4 animate-spin" /> {t("checkout.processing")}
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {/* App Android : le bouton Google Play est LA première option de paiement,
                juste sous le récap, au-dessus des abonnements (demande admin 17/08/2026). */}
            {isTwaApp && !showCardFallback && (
              <Button className="w-full rounded-full" size="lg" data-testid={CHECKOUT.payBtn} onClick={handlePayGooglePlay} disabled={payingGoogle}>
                {payingGoogle ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
                {payingGoogle ? t("checkout.processing") : t("checkout.payGooglePlay")}
              </Button>
            )}
            {/* Upsell abonnement (inchangé). */}
            <div>
              <p className="text-sm font-semibold">{t("subscription.checkoutTitle")}</p>
              <div className="mt-3">
                <SubscriptionPlans
                  resumeOrderId={orderId}
                  onSubscribed={handleSubscribed}
                  onCardOpen={() => setShowCardFallback(false)}
                  collapseSignal={subCollapse}
                  compact
                />
              </div>
            </div>
            {(!isTwaApp || showCardFallback) && (
              <>
                <p className="text-center text-xs uppercase tracking-wide text-muted-foreground">{t("subscription.checkoutOr")}</p>
                <MollieCardPayment
                  pack={total}
                  songPurchase
                  resumeOrderId={orderId}
                  resumeAction="spend"
                  onSuccess={handleCardPaymentSuccess}
                  onCancel={() => setShowCardFallback(false)}
                />
              </>
            )}
          </div>
        )}

        <div className="mt-8 grid grid-cols-3 gap-3 text-center" data-testid={CHECKOUT.trustBadges}>
          <div className="flex flex-col items-center gap-1.5 rounded-xl border border-border bg-card/50 p-3">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <span className="text-[11px] leading-tight text-muted-foreground">{t("checkout.trustSecure")}</span>
          </div>
          <div className="flex flex-col items-center gap-1.5 rounded-xl border border-border bg-card/50 p-3">
            <CreditCard className="h-5 w-5 text-primary" />
            <span className="text-[11px] leading-tight text-muted-foreground">{t("checkout.trustCards")}</span>
          </div>
          <div className="flex flex-col items-center gap-1.5 rounded-xl border border-border bg-card/50 p-3">
            <Headset className="h-5 w-5 text-primary" />
            <span className="text-[11px] leading-tight text-muted-foreground">{t("checkout.trustSupport")}</span>
          </div>
        </div>

        <div className="mt-6">
          <PurchaseStepsReassurance />
        </div>
      </main>
    </div>
  );
}
